import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { Company, InsertCompany, BlogPost, InsertBlogPost } from "@shared/schema";

const API_BASE = "/api";

export interface CompanyInput {
  name: string;
  logo: string;
  logoUrl?: string | null;
  description: string;
  pricePerYear: number;
  excess: number;
  coveredDistance: string;
  roadsideAssistance: boolean;
  homeStart: boolean;
  nationalRecovery: boolean;
  onwardTravel: boolean;
  europecover: boolean;
  responseTime: string;
  rating: number;
  trustpilotRating: number;
  trustpilotReviews: string;
  featured: boolean;
  quoteUrl: string;
}

async function fetchCompanies(): Promise<Company[]> {
  const response = await fetch(`${API_BASE}/companies`);
  if (!response.ok) {
    throw new Error("Failed to fetch companies");
  }
  return response.json();
}

async function createCompany(data: CompanyInput): Promise<Company> {
  const response = await fetch(`${API_BASE}/companies`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error("Failed to create company");
  }
  return response.json();
}

async function updateCompany(id: string, data: Partial<CompanyInput>): Promise<Company> {
  const response = await fetch(`${API_BASE}/companies/${id}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(data),
  });
  if (!response.ok) {
    throw new Error("Failed to update company");
  }
  return response.json();
}

async function deleteCompany(id: string): Promise<void> {
  const response = await fetch(`${API_BASE}/companies/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) {
    throw new Error("Failed to delete company");
  }
}

async function login(password: string): Promise<{ success: boolean }> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!response.ok) {
    throw new Error("Invalid password");
  }
  return response.json();
}

export function useCompanies() {
  return useQuery({
    queryKey: ["companies"],
    queryFn: fetchCompanies,
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CompanyInput> }) =>
      updateCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

export function useDeleteCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["companies"] });
    },
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: login,
  });
}

// Page hooks
import type { Page, PageSection, NavigationMenu, NavigationItem, MediaAsset } from "@shared/schema";

export function usePages() {
  return useQuery({
    queryKey: ["pages"],
    queryFn: async (): Promise<Page[]> => {
      const res = await fetch(`${API_BASE}/pages`);
      if (!res.ok) throw new Error("Failed to fetch pages");
      return res.json();
    },
  });
}

export function usePage(id: string) {
  return useQuery({
    queryKey: ["pages", id],
    queryFn: async (): Promise<Page> => {
      const res = await fetch(`${API_BASE}/pages/${id}`);
      if (!res.ok) throw new Error("Failed to fetch page");
      return res.json();
    },
    enabled: !!id,
  });
}

export function usePageBySlug(slug: string) {
  return useQuery({
    queryKey: ["pages", "slug", slug],
    queryFn: async (): Promise<Page> => {
      const res = await fetch(`${API_BASE}/pages/slug/${slug}`);
      if (!res.ok) throw new Error("Failed to fetch page");
      return res.json();
    },
    enabled: !!slug,
  });
}

export function useCreatePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<Page>): Promise<Page> => {
      const res = await fetch(`${API_BASE}/pages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create page");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pages"] }),
  });
}

export function useUpdatePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<Page> }): Promise<Page> => {
      const res = await fetch(`${API_BASE}/pages/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update page");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pages"] }),
  });
}

export function useDeletePage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_BASE}/pages/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete page");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pages"] }),
  });
}

// Page sections hooks
export function usePageSections(pageId: string) {
  return useQuery({
    queryKey: ["pages", pageId, "sections"],
    queryFn: async (): Promise<PageSection[]> => {
      const res = await fetch(`${API_BASE}/pages/${pageId}/sections`);
      if (!res.ok) throw new Error("Failed to fetch sections");
      return res.json();
    },
    enabled: !!pageId,
  });
}

export function useCreateSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pageId, data }: { pageId: string; data: Partial<PageSection> }): Promise<PageSection> => {
      const res = await fetch(`${API_BASE}/pages/${pageId}/sections`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create section");
      return res.json();
    },
    onSuccess: (_, { pageId }) => queryClient.invalidateQueries({ queryKey: ["pages", pageId, "sections"] }),
  });
}

export function useUpdateSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<PageSection> }): Promise<PageSection> => {
      const res = await fetch(`${API_BASE}/sections/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update section");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pages"] }),
  });
}

export function useDeleteSection() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_BASE}/sections/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete section");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["pages"] }),
  });
}

export function useReorderSections() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pageId, sectionIds }: { pageId: string; sectionIds: string[] }): Promise<void> => {
      const res = await fetch(`${API_BASE}/pages/${pageId}/sections/reorder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sectionIds }),
      });
      if (!res.ok) throw new Error("Failed to reorder sections");
    },
    onSuccess: (_, { pageId }) => queryClient.invalidateQueries({ queryKey: ["pages", pageId, "sections"] }),
  });
}

// Navigation hooks
export function useNavigationMenus() {
  return useQuery({
    queryKey: ["navigation"],
    queryFn: async (): Promise<NavigationMenu[]> => {
      const res = await fetch(`${API_BASE}/navigation`);
      if (!res.ok) throw new Error("Failed to fetch menus");
      return res.json();
    },
  });
}

export function useNavigationMenu(id: string) {
  return useQuery({
    queryKey: ["navigation", id],
    queryFn: async (): Promise<NavigationMenu & { items: NavigationItem[] }> => {
      const res = await fetch(`${API_BASE}/navigation/${id}`);
      if (!res.ok) throw new Error("Failed to fetch menu");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useNavigationMenuByLocation(location: string) {
  return useQuery({
    queryKey: ["navigation", "location", location],
    queryFn: async (): Promise<(NavigationMenu & { items: NavigationItem[] }) | null> => {
      const res = await fetch(`${API_BASE}/navigation/location/${location}`);
      if (res.status === 404) return null;
      if (!res.ok) throw new Error("Failed to fetch menu");
      return res.json();
    },
  });
}

export function useCreateNavigationMenu() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; location: string }): Promise<NavigationMenu> => {
      const res = await fetch(`${API_BASE}/navigation`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create menu");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["navigation"] }),
  });
}

export function useCreateNavigationItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ menuId, data }: { menuId: string; data: Partial<NavigationItem> }): Promise<NavigationItem> => {
      const res = await fetch(`${API_BASE}/navigation/${menuId}/items`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create item");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["navigation"] }),
  });
}

export function useUpdateNavigationItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<NavigationItem> }): Promise<NavigationItem> => {
      const res = await fetch(`${API_BASE}/navigation-items/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update item");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["navigation"] }),
  });
}

export function useDeleteNavigationItem() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_BASE}/navigation-items/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete item");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["navigation"] }),
  });
}

// Media hooks
export function useMediaAssets() {
  return useQuery({
    queryKey: ["media"],
    queryFn: async (): Promise<MediaAsset[]> => {
      const res = await fetch(`${API_BASE}/media`);
      if (!res.ok) throw new Error("Failed to fetch media");
      return res.json();
    },
  });
}

export function useCreateMediaAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<MediaAsset>): Promise<MediaAsset> => {
      const res = await fetch(`${API_BASE}/media`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create media");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["media"] }),
  });
}

export function useDeleteMediaAsset() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_BASE}/media/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete media");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["media"] }),
  });
}

// Settings hooks
export function useSiteSettings() {
  return useQuery({
    queryKey: ["settings"],
    queryFn: async (): Promise<Record<string, string>> => {
      const res = await fetch(`${API_BASE}/settings`);
      if (!res.ok) throw new Error("Failed to fetch settings");
      return res.json();
    },
  });
}

export function useUpdateSiteSetting() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: string }): Promise<void> => {
      const res = await fetch(`${API_BASE}/settings/${key}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ value }),
      });
      if (!res.ok) throw new Error("Failed to update setting");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings"] }),
  });
}

export function useBulkUpdateSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (settings: Record<string, string>): Promise<void> => {
      const res = await fetch(`${API_BASE}/settings/bulk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ settings }),
      });
      if (!res.ok) throw new Error("Failed to update settings");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["settings"] }),
  });
}

// Cookie preferences hooks
export function useCookiePreferences(visitorId: string) {
  return useQuery({
    queryKey: ["cookiePreferences", visitorId],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/cookie-preferences/${visitorId}`);
      if (!res.ok) throw new Error("Failed to fetch preferences");
      return res.json();
    },
    enabled: !!visitorId,
  });
}

export function useSaveCookiePreferences() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { visitorId: string; necessary: boolean; analytics: boolean; marketing: boolean }) => {
      const res = await fetch(`${API_BASE}/cookie-preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to save preferences");
      return res.json();
    },
    onSuccess: (_, { visitorId }) => queryClient.invalidateQueries({ queryKey: ["cookiePreferences", visitorId] }),
  });
}

// Blog hooks
export function useBlogPosts() {
  return useQuery({
    queryKey: ["blog"],
    queryFn: async (): Promise<BlogPost[]> => {
      const res = await fetch(`${API_BASE}/blog`);
      if (!res.ok) throw new Error("Failed to fetch blog posts");
      return res.json();
    },
  });
}

export function usePublishedBlogPosts() {
  return useQuery({
    queryKey: ["blog", "published"],
    queryFn: async (): Promise<BlogPost[]> => {
      const res = await fetch(`${API_BASE}/blog/published`);
      if (!res.ok) throw new Error("Failed to fetch blog posts");
      return res.json();
    },
  });
}

export function useBlogPost(id: string) {
  return useQuery({
    queryKey: ["blog", id],
    queryFn: async (): Promise<BlogPost> => {
      const res = await fetch(`${API_BASE}/blog/${id}`);
      if (!res.ok) throw new Error("Failed to fetch blog post");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useBlogPostBySlug(slug: string) {
  return useQuery({
    queryKey: ["blog", "slug", slug],
    queryFn: async (): Promise<BlogPost> => {
      const res = await fetch(`${API_BASE}/blog/slug/${slug}`);
      if (!res.ok) throw new Error("Failed to fetch blog post");
      return res.json();
    },
    enabled: !!slug,
  });
}

export function useCreateBlogPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<InsertBlogPost>): Promise<BlogPost> => {
      const res = await fetch(`${API_BASE}/blog`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create blog post");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["blog"] }),
  });
}

export function useUpdateBlogPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: Partial<InsertBlogPost> }): Promise<BlogPost> => {
      const res = await fetch(`${API_BASE}/blog/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update blog post");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["blog"] }),
  });
}

export function useDeleteBlogPost() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_BASE}/blog/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete blog post");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["blog"] }),
  });
}

// Click tracking types and hooks
export interface ClickIn {
  id: string;
  source: string | null;
  clickId: string | null;
  keyword: string | null;
  visitorId: string | null;
  userAgent: string | null;
  referrer: string | null;
  landingPage: string | null;
  ipAddress: string | null;
  createdAt: string;
}

export interface AffiliateClickOut {
  id: string;
  companyId: string | null;
  companyName: string | null;
  visitorId: string | null;
  clickInId: string | null;
  destinationUrl: string | null;
  createdAt: string;
}

export function useClickIns() {
  return useQuery({
    queryKey: ["tracking", "click-ins"],
    queryFn: async (): Promise<ClickIn[]> => {
      const res = await fetch(`${API_BASE}/tracking/click-ins`);
      if (!res.ok) throw new Error("Failed to fetch click-ins");
      return res.json();
    },
  });
}

export function useAffiliateClickOuts() {
  return useQuery({
    queryKey: ["tracking", "click-outs"],
    queryFn: async (): Promise<AffiliateClickOut[]> => {
      const res = await fetch(`${API_BASE}/tracking/click-outs`);
      if (!res.ok) throw new Error("Failed to fetch click-outs");
      return res.json();
    },
  });
}

export function useRecordClickIn() {
  return useMutation({
    mutationFn: async (data: Partial<ClickIn>): Promise<ClickIn> => {
      const res = await fetch(`${API_BASE}/tracking/click-in`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to record click-in");
      return res.json();
    },
  });
}

export async function updateTimeOnPage(id: string, timeOnPage: number): Promise<void> {
  const blob = new Blob([JSON.stringify({ timeOnPage })], { type: "application/json" });
  navigator.sendBeacon(`${API_BASE}/tracking/click-in/${id}/time`, blob);
}

export function useRecordClickOut() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Partial<AffiliateClickOut>): Promise<AffiliateClickOut> => {
      const res = await fetch(`${API_BASE}/tracking/click-out`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to record click-out");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tracking"] }),
  });
}

// Cover Finder Sessions
export interface CoverFinderSession {
  id: string;
  visitorId: string | null;
  vehicleType: string | null;
  vehicleUsage: string | null;
  drivingArea: string | null;
  vehicleAge: string | null;
  assistanceLevel: string | null;
  europeanCover: string | null;
  extraCover: string[] | null;
  suggestedProviders: string[] | null;
  createdAt: string;
}

export function useCoverFinderSessions() {
  return useQuery<CoverFinderSession[]>({
    queryKey: ["cover-finder-sessions"],
    queryFn: async () => {
      const res = await fetch(`${API_BASE}/cover-finder-sessions`);
      if (!res.ok) throw new Error("Failed to fetch sessions");
      return res.json();
    },
  });
}

export function useRecordCoverFinderSession() {
  return useMutation({
    mutationFn: async (data: Partial<CoverFinderSession>): Promise<CoverFinderSession> => {
      const res = await fetch(`${API_BASE}/cover-finder-sessions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to record session");
      return res.json();
    },
  });
}

// Contact Messages
export interface ContactMessage {
  id: string;
  name: string;
  email: string;
  phone: string | null;
  subject: string;
  message: string;
  isRead: boolean;
  createdAt: string;
}

export function useContactMessages() {
  return useQuery({
    queryKey: ["contact-messages"],
    queryFn: async (): Promise<ContactMessage[]> => {
      const res = await fetch(`${API_BASE}/contact-messages`);
      if (!res.ok) throw new Error("Failed to fetch contact messages");
      return res.json();
    },
  });
}

export function useMarkMessageRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, isRead }: { id: string; isRead: boolean }): Promise<ContactMessage> => {
      const res = await fetch(`${API_BASE}/contact-messages/${id}/read`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ isRead }),
      });
      if (!res.ok) throw new Error("Failed to update message");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contact-messages"] }),
  });
}

export function useDeleteContactMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_BASE}/contact-messages/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete message");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["contact-messages"] }),
  });
}

// Tags
export interface Tag {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  color: string | null;
  createdAt: string | null;
}

export function useTags() {
  return useQuery({
    queryKey: ["tags"],
    queryFn: async (): Promise<Tag[]> => {
      const res = await fetch(`${API_BASE}/tags`);
      if (!res.ok) throw new Error("Failed to fetch tags");
      return res.json();
    },
  });
}

export function useTag(id: string) {
  return useQuery({
    queryKey: ["tags", id],
    queryFn: async (): Promise<Tag> => {
      const res = await fetch(`${API_BASE}/tags/${id}`);
      if (!res.ok) throw new Error("Failed to fetch tag");
      return res.json();
    },
    enabled: !!id,
  });
}

export function useCreateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: { name: string; slug: string; description?: string; color?: string }): Promise<Tag> => {
      const res = await fetch(`${API_BASE}/tags`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create tag");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tags"] }),
  });
}

export function useUpdateTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: { id: string; name?: string; slug?: string; description?: string; color?: string }): Promise<Tag> => {
      const res = await fetch(`${API_BASE}/tags/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update tag");
      return res.json();
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tags"] }),
  });
}

export function useDeleteTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_BASE}/tags/${id}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete tag");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tags"] }),
  });
}

// Page Tags
export function usePageTags(pageId: string) {
  return useQuery({
    queryKey: ["pages", pageId, "tags"],
    queryFn: async (): Promise<Tag[]> => {
      const res = await fetch(`${API_BASE}/pages/${pageId}/tags`);
      if (!res.ok) throw new Error("Failed to fetch page tags");
      return res.json();
    },
    enabled: !!pageId,
  });
}

export function useSetPageTags() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ pageId, tagIds }: { pageId: string; tagIds: string[] }): Promise<Tag[]> => {
      const res = await fetch(`${API_BASE}/pages/${pageId}/tags`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tagIds }),
      });
      if (!res.ok) throw new Error("Failed to set page tags");
      return res.json();
    },
    onSuccess: (_, { pageId }) => {
      queryClient.invalidateQueries({ queryKey: ["pages", pageId, "tags"] });
    },
  });
}

export function usePagesByTag(tagId: string) {
  return useQuery({
    queryKey: ["tags", tagId, "pages"],
    queryFn: async (): Promise<Page[]> => {
      const res = await fetch(`${API_BASE}/tags/${tagId}/pages`);
      if (!res.ok) throw new Error("Failed to fetch pages by tag");
      return res.json();
    },
    enabled: !!tagId,
  });
}

export type Advert = {
  id: string;
  title: string;
  imageUrl: string;
  destinationUrl: string;
  isActive: boolean;
  placement: string;
  sortOrder: number;
  createdAt: string;
};

export function useAdverts() {
  return useQuery({
    queryKey: ["adverts"],
    queryFn: async (): Promise<Advert[]> => {
      const res = await fetch(`${API_BASE}/adverts`);
      if (!res.ok) throw new Error("Failed to fetch adverts");
      return res.json();
    },
  });
}

export function useActiveAdverts(placement?: string) {
  return useQuery({
    queryKey: ["adverts", "active", placement],
    queryFn: async (): Promise<Advert[]> => {
      const url = placement
        ? `${API_BASE}/adverts/active?placement=${encodeURIComponent(placement)}`
        : `${API_BASE}/adverts/active`;
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch active adverts");
      return res.json();
    },
  });
}

export function useCreateAdvert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: Omit<Advert, "id" | "createdAt">): Promise<Advert> => {
      const res = await fetch(`${API_BASE}/adverts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to create advert");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adverts"] });
    },
  });
}

export function useUpdateAdvert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...data }: Partial<Advert> & { id: string }): Promise<Advert> => {
      const res = await fetch(`${API_BASE}/adverts/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
      if (!res.ok) throw new Error("Failed to update advert");
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adverts"] });
    },
  });
}

export function useDeleteAdvert() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const res = await fetch(`${API_BASE}/adverts/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error("Failed to delete advert");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["adverts"] });
    },
  });
}
